import React, { useState, useMemo, useCallback } from 'react'
import { Route, useHistory } from 'react-router-dom'
import { ISelectOption } from 'components/forms/Selects/SelectOption'
import orderBy from 'lodash/orderBy'
import times from 'lodash/times'
import AddIcon from 'transfix-icons/build/AddIcon'
import SortArrowDownIcon from 'transfix-icons/build/SortArrowDownIcon'
import SortUpIcon from 'transfix-icons/build/SortUpIcon'
import { Badge } from 'transfix-ui/components/Badge'
import { ISingleSelectProps, SingleSelect } from 'transfix-ui/components/SingleSelect'
import { Skeleton } from 'transfix-ui/core'
import Box from 'transfix-ui/core/Box'
import Typography from 'transfix-ui/core/Typography'
import { ResponsiveLayout } from 'apps/fleet-planner/components/layout/ResponsiveLayout'
import SavedLaneFormModal from 'apps/fleet-planner/components/saved-lanes/SavedLaneFormModal'
import SavedLanesRow from 'apps/fleet-planner/components/saved-lanes/SavedLaneRow'
import { useSavedLanesContext } from 'apps/fleet-planner/context/SavedLanesContext'
import { BookableShipmentSavedSearch } from 'apps/fleet-planner/types'
import SavedLanesPageEmpty from './components/SavedLanesPageEmpty'
import { useSavedLanesStyles } from './useSavedLanesStyles'

enum ISortTypes {
  asc = 'asc',
  desc = 'desc',
}

const opts: ISingleSelectProps['options'] = [
  {
    label: 'Added (Most Recent)',
    value: ISortTypes.desc,
  },
  {
    label: 'Added (Oldest)',
    value: ISortTypes.asc,
  },
]

export const SavedLanesPage = () => {
  const classes = useSavedLanesStyles()
  const history = useHistory()

  const { savedLanes = [], loading, updateSavedLanesState } = useSavedLanesContext()

  const [selectedSort, setSelectedSort] = useState<ISortTypes>(ISortTypes.desc)

  const sortHandler = (sortValue?: ISelectOption) => {
    const { value } = sortValue || {}
    if (value !== undefined) {
      setSelectedSort(value.toString())
    }
  }

  const sortedSavedLanes = useMemo(() => {
    return orderBy(savedLanes, ['createdAt'], [selectedSort])
  }, [selectedSort, savedLanes])

  const openModalHandler = useCallback(() => {
    history.replace(`/transfix-lanes/add-alert${history.location.search}`)
  }, [history])

  const closeModalHandler = useCallback(() => {
    history.replace(`/transfix-lanes${history.location.search}`)
  }, [history])

  const successHandler = useCallback(
    (updatedSavedLane: BookableShipmentSavedSearch) => {
      closeModalHandler()
      updateSavedLanesState(updatedSavedLane)
    },
    [updateSavedLanesState, closeModalHandler]
  )
  return (
    <ResponsiveLayout
      title='Saved Lanes'
      primaryActionProps={{
        label: 'Add Saved Lane',
        buttonIcon: AddIcon,
        fabIcon: AddIcon,
        onClick: openModalHandler,
        desktopButtonProps: {
          'data-test': 'saved-lane-create-button',
        },
      }}
      showBackButton>
      <Box px={4}>
        <div className={classes.titleWrapper}>
          <div className={classes.title}>
            <Badge
              dataTest='saved-lanes-count'
              label={`${savedLanes.length}`}
              color='gray'
              size='small'
            />
            <Typography variant='subtitle1'>Saved Lanes</Typography>
          </div>
          <SingleSelect
            id='savedLanesSort'
            dataTest='savedLanesSort'
            onSelect={sortHandler}
            label='Sort by'
            options={opts}
            selected={selectedSort}
            clearable={false}
            icon={
              selectedSort === ISortTypes.desc ? (
                <SortUpIcon size='large' />
              ) : (
                <SortArrowDownIcon size='large' />
              )
            }
          />
        </div>
        <Box display='flex' flexDirection='column' gap={1.25}>
          {loading &&
            times(5).map(item => (
              <Skeleton key={item} variant='rectangular' width='100%' height='97px' />
            ))}
          {!loading &&
            savedLanes.length > 0 &&
            sortedSavedLanes.map(savedLane => (
              <SavedLanesRow savedLane={savedLane} key={`saved-lanes-row-${savedLane.id}`} />
            ))}
          {!loading && savedLanes.length === 0 && <SavedLanesPageEmpty onAdd={openModalHandler} />}
        </Box>
      </Box>
      <Route exact path='/transfix-lanes/add-alert'>
        <SavedLaneFormModal onClose={closeModalHandler} onSuccess={successHandler} />
      </Route>
    </ResponsiveLayout>
  )
}

export default SavedLanesPage
